/**
 * Phase 3 — DesignVariableSelector
 *
 * Lists the registry variables grouped by category. Each row carries
 * a checkbox (include in study), min/max/step inputs (pre-filled from
 * the registry default), and an override-mode badge.
 *
 * Stays uncontrolled-friendly: the parent passes the current `selected`
 * map and an `onChange(map)` callback. The map is keyed by the
 * variable's dotted ``path`` so it slots straight into the
 * CreateOptimisationStudyRequest body.
 */
import { useMemo } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const CATEGORY_ORDER = ["interceptor", "payload", "sensor", "guidance", "environment"];
const CATEGORY_LABELS = {
    interceptor: "Interceptor",
    payload: "Payload",
    sensor: "Sensor",
    guidance: "Guidance",
    environment: "Environment",
};

export default function DesignVariableSelector({
    registry,
    selected,
    onChange,
}) {
    const groups = useMemo(() => {
        const acc = {};
        for (const v of registry || []) {
            const cat = v.category || "other";
            (acc[cat] = acc[cat] || []).push(v);
        }
        for (const k of Object.keys(acc)) {
            acc[k].sort((a, b) => (a.label || a.path).localeCompare(b.label || b.path));
        }
        return acc;
    }, [registry]);

    const ordered = useMemo(() => {
        const remaining = new Set(Object.keys(groups));
        const out = [];
        for (const cat of CATEGORY_ORDER) {
            if (remaining.has(cat)) {
                out.push([cat, groups[cat]]);
                remaining.delete(cat);
            }
        }
        for (const cat of [...remaining].sort()) {
            out.push([cat, groups[cat]]);
        }
        return out;
    }, [groups]);

    const toggleVariable = (v) => {
        const next = { ...(selected || {}) };
        if (next[v.path]) {
            delete next[v.path];
        } else {
            next[v.path] = {
                path: v.path,
                min: v.default_min,
                max: v.default_max,
                step: v.default_step,
                cost_weight: v.cost_weight,
            };
        }
        onChange(next);
    };

    const patchVariable = (path, fields) => {
        const next = { ...(selected || {}) };
        if (!next[path]) return;
        next[path] = { ...next[path], ...fields };
        onChange(next);
    };

    const selCount = Object.keys(selected || {}).length;

    return (
        <div className="space-y-3" data-testid="variable-selector">
            <div className="flex items-baseline gap-2">
                <span className="text-[12px] text-ux-text-3 tabular-nums">
                    {selCount} of {(registry || []).length} variables selected
                </span>
            </div>

            {ordered.length === 0 && (
                <p className="text-[11px] text-ux-text-3 italic">
                    No design variables in the registry yet. An admin can seed them
                    via /api/optimisation/variables.
                </p>
            )}

            {ordered.map(([cat, vars]) => (
                <fieldset
                    key={cat}
                    className="rounded-md border border-ux-border-1 bg-ux-bg-1 p-3 space-y-2"
                    data-testid={`variable-group-${cat}`}
                >
                    <legend className="text-[11px] uppercase tracking-wider text-ux-text-3 px-1">
                        {CATEGORY_LABELS[cat] || cat}
                    </legend>
                    <div className="space-y-2">
                        {vars.map((v) => {
                            const isOn = !!selected?.[v.path];
                            const sel = selected?.[v.path];
                            return (
                                <VariableRow
                                    key={v.key}
                                    variable={v}
                                    on={isOn}
                                    sel={sel}
                                    onToggle={() => toggleVariable(v)}
                                    onPatch={(fields) => patchVariable(v.path, fields)}
                                />
                            );
                        })}
                    </div>
                </fieldset>
            ))}
        </div>
    );
}

function VariableRow({ variable, on, sel, onToggle, onPatch }) {
    const id = `var-${variable.key.replace(/[^a-z0-9]+/gi, "-")}`;
    const numField = (key, value, placeholder) => (
        <Input
            type="number"
            value={value ?? ""}
            onChange={(e) =>
                onPatch({
                    [key]: e.target.value === "" ? null : parseFloat(e.target.value),
                })
            }
            placeholder={placeholder ?? ""}
            disabled={!on}
            className="bg-ux-bg-2 max-w-[88px] h-7 px-2 tabular-nums text-[12px]"
            data-testid={`var-${variable.key}-${key}`}
        />
    );

    const tier = variable.allowed_tiers?.[0] || "any";

    return (
        <div className="flex items-start gap-3 rounded p-2 hover:bg-ux-bg-2/40">
            <Checkbox
                id={id}
                checked={on}
                onCheckedChange={onToggle}
                data-testid={`var-${variable.key}-toggle`}
                className="mt-1"
            />
            <Label htmlFor={id} className="flex-1 cursor-pointer space-y-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[13px] text-ux-text-1 font-medium truncate">
                        {variable.label || variable.path}
                    </span>
                    <span
                        className={[
                            "text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded",
                            variable.override_mode === "engine_inline"
                                ? "bg-ux-accent-soft text-ux-accent"
                                : "bg-ux-bg-3 text-ux-text-3",
                        ].join(" ")}
                    >
                        {variable.override_mode}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-ux-text-3">
                        tier: {tier}
                    </span>
                </div>
                <p className="text-[11px] text-ux-text-3 font-mono truncate">
                    {variable.path} · default {variable.default_min}–{variable.default_max}
                </p>
                {variable.description && (
                    <p className="text-[11px] text-ux-text-3">{variable.description}</p>
                )}
            </Label>
            <div className="flex items-center gap-2">
                {numField("min", sel?.min, String(variable.default_min))}
                <span className="text-[11px] text-ux-text-3">…</span>
                {numField("max", sel?.max, String(variable.default_max))}
                {numField("step", sel?.step, String(variable.default_step ?? "auto"))}
            </div>
        </div>
    );
}
